import { useTranslation, Translation } from 'react-i18next';
import React, { memo, useCallback, useEffect, useState, useMemo, ReactNode } from 'react';
import cx, { clsx } from 'clsx';

import { Button, ButtonProps, HelperTooltip } from '@/components';
import { login, queryClient, isOfficalAddress, toastError } from '@/utils';
import {
  useCurrentAddressInfo,
  updateUserDetailQuery,
  useFollowMutation,
  useOfficialFollow,
  useDeviceTypeIsMobile,
  useTheme,
} from '@/hooks';
import { setQueryFeedAllUsersInList } from '@/views/Stream/hooks/list';
import style from './index.module.css';
import {
  IconRcFollow,
  IconRcFollowingLight,
  IconRcFollowingDark,
  IconRcFollowingButton,
  IconRcFriend,
  IconRcUnFollow,
} from '@/assets/follow';

import { moreFeatureModalTip } from '../shared/MobileModalTip';
import { openFollowLimitModal } from './components/FollowLimitModal/FollowLimitModal';
import { useUnionFollow } from './hook';

const followErrorMsgHandler = (err: any) => {
  const errorMsg = String(err) || 'failed';

  if (errorMsg.includes('need a Web3 ID to increase your following limit to')) {
    openFollowLimitModal(errorMsg);
  } else {
    toastError(err || 'failed');
  }
};

const followUserInFeedHandler = (pre?: UnionAccount) => {
  if (pre) {
    pre.is_following = !pre.is_following;
  }
};

export interface Relationship {
  isFollowing: boolean;
  isFollowed?: boolean;
  couldFollowBack?: boolean;
  isFriend: boolean;
  status: string;
  text: ReactNode;
}

const statusTextOnChat = {
  friend: <Translation>{t => t('FollowButton.friend')}</Translation>,
  followBack: <Translation>{t => t('FollowButton.follow-back')}</Translation>,
  following: <Translation>{t => t('FollowButton.following')}</Translation>,
  follow: <Translation>{t => t('FollowButton.follow')}</Translation>,
} as const;

function getRelationship(isFollowingLocal?: boolean, isFollowed?: boolean): Relationship {
  const isFollowing = Boolean(isFollowingLocal);
  const isFriend = Boolean(isFollowing && isFollowed);

  const couldFollowBack = !isFollowing && isFollowed;

  const status = isFriend
    ? 'friend'
    : couldFollowBack
      ? 'followBack'
      : isFollowing
        ? 'following'
        : ('follow' as const);

  return {
    isFollowing,
    isFollowed,
    couldFollowBack,
    isFriend,
    status,
    text: statusTextOnChat[status],
  };
}

type FollowButtonProps = {
  address: string;
  className?: string;
  userInfo?: UserInfo;
  restParams?: Record<string, string | number | boolean | undefined>;

  type?: 'icon';
  operateCallback?: (isFollowing: boolean) => void;
  // 展示样式，渐变
  gradient?: boolean;
  onClick?: (isFollowing: boolean) => void;
};

export const FollowButton = memo(
  ({
    address,
    className,
    userInfo,
    type,
    operateCallback,
    gradient,
    onClick,
    restParams,
  }: FollowButtonProps) => {
    const { t } = useTranslation();
    const { isDark } = useTheme();
    const isFollowing = userInfo?.is_following;
    const [_isFollowing, setFollowing] = useState(isFollowing);
    const [followCount, setFollowCount] = useState(userInfo?.follower_count || 0);
    const { address: currentAddress, isVerified } = useCurrentAddressInfo();
    const { mutateAsync, isLoading } = useFollowMutation(address);
    const ensuredFollow = useMemo(() => typeof isFollowing !== 'undefined', [isFollowing]);
    const { isFriend } = useMemo(
      () => getRelationship(_isFollowing, !!userInfo?.is_followed),
      [_isFollowing, userInfo],
    );
    const IconRcFollowing = useMemo(
      () => (isDark ? IconRcFollowingDark : IconRcFollowingLight),
      [isDark],
    );

    useEffect(() => {
      setFollowCount(userInfo?.follower_count || 0);
    }, [userInfo?.follower_count]);

    useEffect(() => {
      setFollowing(isFollowing);
    }, [isFollowing]);

    const handleFollowClick = useCallback(
      async (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        e.preventDefault();
        onClick?.(!!_isFollowing);

        if (!address || !isVerified) {
          login();
          return;
        }

        try {
          const res = await mutateAsync({ isFollowing: _isFollowing!, ...restParams });

          if (!res?.is_success) {
            throw new Error('failed');
          }

          setFollowing(!_isFollowing);

          currentAddress &&
            setQueryFeedAllUsersInList(currentAddress, address, followUserInFeedHandler);

          const next: Partial<UserInfo> = {
            id: address,
            is_following: !_isFollowing,
          };

          if (userInfo) {
            // next.tvf = userInfo.tvf + (currentUserInfo?.desc?.usd_value || 0) * (_isFollowing ? -1 : 1)
            const nextCount = followCount + (_isFollowing ? -1 : 1);
            setFollowCount(nextCount);
            next.follower_count = nextCount;
          }

          updateUserDetailQuery(next as any);
          operateCallback?.(!_isFollowing);
        } catch (error) {
          followErrorMsgHandler(error);
        }
      },
      [
        onClick,
        _isFollowing,
        address,
        isVerified,
        mutateAsync,
        restParams,
        currentAddress,
        operateCallback,
        userInfo,
        followCount,
      ],
    );

    const isIconType = type === 'icon';

    if (address === currentAddress) {
      return null;
    }

    return ensuredFollow ? (
      <Button
        type={isIconType ? 'text' : _isFollowing ? 'default' : 'primary'}
        ghost
        disabled={!ensuredFollow}
        loading={isLoading}
        className={cx(
          style.followBtn,
          gradient && !_isFollowing && style.gradient,
          isIconType && style.isIconType,
          isFriend && style.isFriend,
          className,
        )}
        onClick={handleFollowClick}>
        {_isFollowing ? (
          <>
            <HelperTooltip disable={!isIconType} title={t('FollowButton.follow')} placement="top">
              <div className={style.follwing}>
                {isFriend ? (
                  <div className={style.followBtnInner}>
                    <IconRcFriend className={style.followBtnPrefixIcon} />
                    {isIconType ? null : t('FollowButton.friend')}
                  </div>
                ) : (
                  <div className={style.followBtnInner}>
                    <IconRcFollowing className={style.followBtnPrefixIcon} />
                    {isIconType ? null : t('FollowButton.following')}
                  </div>
                )}
              </div>
            </HelperTooltip>
            <HelperTooltip disable={!isIconType} title={t('FollowButton.1trw9m2')} placement="top">
              <div className={style.unfollow}>
                <div className={style.followBtnInner}>
                  <IconRcUnFollow className={style.followBtnPrefixIcon} />
                  {isIconType ? null : t('FollowButton.1trw9m2')}
                </div>
              </div>
            </HelperTooltip>
          </>
        ) : (
          <HelperTooltip disable={!isIconType} title={t('FollowButton.follow')} placement="top">
            <div className={style.followBtnInner}>
              <IconRcFollow
                className={clsx(
                  style.followBtnPrefixIcon,
                  gradient ? style.colorWhite : style.colorPrimary,
                )}
              />
              {isIconType ? null : t('FollowButton.follow')}
            </div>
          </HelperTooltip>
        )}
      </Button>
    ) : null;
  },
);

export const UnionFollowButton = memo(
  ({
    address,
    className,
    isFollowing = false,
    isFollowed = false,
    restParams,
    type,
    followingType,
    operateCallback,
    disabled,
    ghost = true,
    onFollowCallback,
    prefix,
  }: {
    address: string;
    className?: string;
    isFollowing?: boolean;
    isFollowed?: boolean;
    restParams?: Record<string, string | number | boolean | undefined>;
    type?: ButtonProps['type'];
    followingType?: ButtonProps['type'];
    operateCallback?: (isFollowing: boolean) => void;
    disabled?: boolean;
    ghost?: boolean;
    children?: ReactNode;
    onFollowCallback?: (isSuccess: boolean) => void;
    prefix?: ReactNode;
  }) => {
    const { t } = useTranslation();
    const { isDark } = useTheme();

    const ensuredFollow = useMemo(() => typeof isFollowing !== 'undefined', [isFollowing]);
    const { address: currentAddress } = useCurrentAddressInfo();
    const isMe = currentAddress === address;
    const { isLoading, isLocalFollowing, followMutate } = useUnionFollow({
      address,
      isFollowing,
      operateCallback,
      onFollowCallback,
      restParams,
    });

    const { isFriend } = useMemo(
      () => getRelationship(isLocalFollowing, isFollowed),
      [isLocalFollowing, isFollowed],
    );

    const IconRcFollowing = useMemo(
      () => (isDark ? IconRcFollowingDark : IconRcFollowingLight),
      [isDark],
    );

    return isMe ? null : (
      <Button
        type={isLocalFollowing ? followingType || 'gray' : type || 'dialog'}
        ghost={ghost}
        disabled={disabled || !ensuredFollow}
        loading={isLoading}
        className={cx(style.followBtn, className, disabled && style.disabled)}
        onClick={followMutate}>
        {ensuredFollow ? (
          isLocalFollowing ? (
            <>
              <span className={style.follwing}>
                {isFriend ? (
                  <div className={style.followBtnInner}>
                    {prefix ? prefix : <IconRcFriend className={style.followBtnPrefixIcon} />}
                    {t('FollowButton.friend')}
                  </div>
                ) : (
                  <div className={style.followBtnInner}>
                    {prefix ? prefix : <IconRcFollowing className={style.followBtnPrefixIcon} />}
                    {t('FollowButton.following')}
                  </div>
                )}
              </span>
              <span className={style.unfollow}>
                <div className={style.followBtnInner}>
                  {prefix ? prefix : <IconRcUnFollow className={style.followBtnPrefixIcon} />}
                  {t('FollowButton.1trw9m2')}
                </div>
              </span>
            </>
          ) : (
            <div className={style.followBtnInner}>
              {prefix ? prefix : <IconRcFollow className={clsx(style.followBtnPrefixIcon)} />}
              {t('FollowButton.follow')}
            </div>
          )
        ) : null}
      </Button>
    );
  },
);

export const UnionFollowButtonV2 = memo(
  ({
    address,
    className,
    isFollowing = false,
    restParams,
    onFollowCallback,
  }: {
    address: string;
    className?: string;
    isFollowing?: boolean;
    isFollowed?: boolean;
    restParams?: Record<string, string | number | boolean | undefined>;
    onFollowCallback?: (isSuccess: boolean) => void;
  }) => {
    const { t } = useTranslation();
    const { address: currentAddress, isVerified } = useCurrentAddressInfo();
    const isMe = useMemo(() => currentAddress === address, [address, currentAddress]);
    const isMobile = useDeviceTypeIsMobile();
    const isOfficialAccount = useMemo(() => isOfficalAddress(address), [address]);
    const [_isFollowing, setFollowing] = useState(isFollowing);
    const { mutateAsync, isLoading } = useFollowMutation(address);
    const { mutateAsync: followOfficial, isLoading: isfollowingOfficial } =
      useOfficialFollow(address);

    const ensuredFollow = useMemo(() => typeof isFollowing !== 'undefined', [isFollowing]);
    const [forceShowFollowButton, setForceShowFollowButton] = useState(false);

    useEffect(() => {
      setFollowing(isFollowing);
    }, [isFollowing]);

    const handleFollowClick = useCallback(
      async (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        e.preventDefault();
        if (isMobile) {
          moreFeatureModalTip();
          return;
        }
        if (_isFollowing) {
          return;
        }

        if (!address || !isVerified) {
          login();
          onFollowCallback?.(false);
          return;
        }

        // force show button for few second
        setForceShowFollowButton(true);
        setTimeout(() => {
          setForceShowFollowButton(false);
        }, 1000);
        try {
          const params = { isFollowing: _isFollowing, ...restParams };
          const res = await (isOfficialAccount ? followOfficial : mutateAsync)(params);

          if (!res?.is_success) {
            onFollowCallback?.(false);
            throw new Error('failed');
          }
          onFollowCallback?.(true);
          setFollowing(!_isFollowing);

          currentAddress &&
            setQueryFeedAllUsersInList(currentAddress, address, followUserInFeedHandler);
          if (isOfficialAccount) {
            const query = ['/official', String(address), currentAddress];
            const queryData = queryClient.getQueryData<{ official: OfficialAccount }>(query);
            if (queryData) {
              const count = queryData.official.follower_count ?? 0;
              queryClient.setQueryData(query, {
                ...queryData,
                official: {
                  ...queryData.official,
                  is_following: !isFollowing,
                  follower_count: !isFollowing ? count + 1 : count - 1,
                },
              });
            }
          } else {
            updateUserDetailQuery({
              id: address,
              is_following: !_isFollowing,
            });
          }
        } catch (error) {
          onFollowCallback?.(false);
          followErrorMsgHandler(error);
        }
      },
      [
        isMobile,
        _isFollowing,
        address,
        isVerified,
        restParams,
        isOfficialAccount,
        followOfficial,
        mutateAsync,
        currentAddress,
        isFollowing,
        onFollowCallback,
      ],
    );

    return isMe || (ensuredFollow && _isFollowing && !forceShowFollowButton) ? null : (
      <Button
        type={'text'}
        ghost
        disabled={!ensuredFollow && _isFollowing}
        loading={isLoading || isfollowingOfficial}
        className={cx(style.followBtnV2, _isFollowing && style.isFollowing, className)}
        onClick={handleFollowClick}>
        {ensuredFollow ? _isFollowing ? <IconRcFollowingButton /> : t('FollowButton.follow') : null}
      </Button>
    );
  },
);

export default FollowButton;

export * from './BaseFollowButton';
export * from './hook';
